import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, map } from 'rxjs/operators';
import { LocalStorage } from 'angular-web-storage';

import { Subject } from 'rxjs';
import { ListResponse } from '../../../../../../models/list-response';
import { AccountService } from '../../../../../../services/account/account.service';
import { addFiltersToGetParams, dateStringFromISO8601 } from '../../../../../../utils';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import {
  getMembershipDiscountApplyMethodDisplay,
  loadMembershipDiscountFiltersFromGetParams,
  MembershipDiscount, MembershipDiscountFilters
} from '../../../../../../models/membership';
import { MembershipService } from '../../../../../../services/membership/membership.service';
import { MembershipDiscountApplyMethod, MembershipDiscountType } from '../../../../../../models/membership-enums';


@Component({
  selector: 'app-membership-discounts',
  templateUrl: './membership-discounts.component.html',
  styleUrls: ['./membership-discounts.component.css']
})
export class MembershipDiscountsComponent implements OnInit {
  public pageNumber = 1;
  @LocalStorage() public pageSize;
  public filters: MembershipDiscountFilters = {};

  public filtersChangedSubject = new Subject<null>();

  public resultList: ListResponse<MembershipDiscount>;

  public loadingResults = false;

  public serverError: number = null;

  // Make browser history available to the template.
  public history: History;

  public dateStringFromISO8601 = dateStringFromISO8601;
  public MembershipDiscountType = MembershipDiscountType;
  public MembershipDiscountApplyMethod = MembershipDiscountApplyMethod;
  public getMembershipDiscountApplyMethodDisplay = getMembershipDiscountApplyMethodDisplay;

  // The number of list requests. Used to ensure that only the most recent request
  // is displayed, in the event of concurrent requests (due to, e.g., filtering).
  private listRequestCount = 0;

  constructor(
    public accountService: AccountService,
    public orgConfig: OrganizationConfigService,
    private activatedRoute: ActivatedRoute,
    private membershipService: MembershipService,
    private modalService: NgbModal,
    private router: Router,
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    if (!this.pageSize) {
      this.pageSize = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSize = this.pageSize;

    // Make browser history available to the template.
    this.history = history;

    // Handle changes to the filters by reloading the results, subject to debounce.
    this.filtersChangedSubject.pipe(
      map(() => { this.loadingResults = true; }),
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadDiscounts();
      addFiltersToGetParams(
        this.filters,
        this.router,
        this.activatedRoute
      );
    });

    // Load initial query parameters from URL if present.
    this.filters = loadMembershipDiscountFiltersFromGetParams(this.activatedRoute);

    this.loadDiscounts();
  }

  /**
   * Load the page of results corresponding to the current page and filter selections.
   */
  public loadDiscounts() {
    this.loadingResults = true;

    // Increment the request count and save a local copy.
    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;

    this.membershipService
      .getMembershipDiscounts(this.pageNumber, this.pageSize, this.filters)
      .then(response => {
        // Only update the results if there has not been a more recent request initiated.
        if (thisRequestCount === this.listRequestCount) {
          this.resultList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingResults = false;
        }
      });
  }

  /**
   * Update the current page number to the given value and load the page of
   * results.
   *
   * @param newPageNumber - The new page number.
   */
  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.loadDiscounts();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.resultList) { return 1; }
    return Math.ceil(this.resultList.count / this.pageSize);
  }

  /**
   * Handle a change in the filters.
   */
  public handleFilterChange() {
    this.pageNumber = 1;

    this.filtersChangedSubject.next();
  }

  /**
   * Whether a non-zero number of results have been loaded.
   */
  public resultsPresent(): boolean {
    return (
      this.resultList &&
      this.resultList.results &&
      (this.resultList.results.length > 0)
    );
  }
}
